import { useActionSheet } from "@expo/react-native-action-sheet";
import {
  isWeb,
  useDeleteProfileMutation,
  useGetUserProfileQuery,
  useUpdateCustomerProfileMutation,
} from "@stepapp/core";
import {
  Divider,
  Input,
  palette,
  Switch,
  spacing,
  Touchable,
  TouchableInput,
  Typography,
} from "@stepapp/ui";
import { Link } from "expo-router";
import { Fragment, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import { useLogout } from "../../src/components/HomeHeader/useLogout";
import { useAppDispatch, useAppLinks, useAppSelector } from "../../src/hooks";
import { AppStorage } from "../../src/services/AppStorage";
import { useDisplayIntercom } from "../../src/services/Intercom";
import { editUserData } from "../../src/store/user";
import ChangeEmailModal from "./ChangeEmailModal";

type FormValues = {
  email: string;
  newsletterNotifications: boolean;
  phoneNumber: string;
  pushNotifications: boolean;
  smsNotifications: boolean;
};

const Settings = () => {
  const { t } = useTranslation();
  const { showActionSheetWithOptions } = useActionSheet();
  const { PrivacyPolicy, TermsOfUse, CancellationRules } = useAppLinks();
  const countryCode = AppStorage().get("countryCode");
  const displayIntercom = useDisplayIntercom(countryCode);
  const [isChangeEmailModalVisible, setIsChangeEmailModalVisible] = useState(false);

  const handleLogOut = useLogout();
  const appDispatch = useAppDispatch();
  const user = useAppSelector(({ user }) => user);

  const [deleteProfile] = useDeleteProfileMutation();
  const [updateCustomerProfile] = useUpdateCustomerProfileMutation();
  const { refetch } = useGetUserProfileQuery({
    variables: {
      isCustomerProfile: true,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const links = [
    { href: PrivacyPolicy, label: t("screens.settings.privacyPolicy") },
    { href: TermsOfUse, label: t("screens.login.termsOfUse") },
    { href: CancellationRules, label: t("screens.settings.cancellationRules") },
  ];

  const notificationSettings = [
    {
      key: "pushNotifications",
      labelKey: t("screens.settings.pushNotifications"),
      descriptionKey: t("screens.settings.informationAboutReservation"),
    },
    {
      key: "smsNotifications",
      labelKey: t("screens.settings.smsNotifications"),
      descriptionKey: t("screens.settings.reminderAboutReservation"),
    },
    {
      key: "newsletterNotifications",
      labelKey: t("screens.settings.newsletterAndPromotions"),
      descriptionKey: t("screens.settings.discountsAndPromotions"),
    },
  ] as const;

  const defaultValues = {
    email: user?.email,
    phoneNumber: user?.profile?.phoneNumber,
    newsletterNotifications: user?.notifications?.newsletterNotifications,
    pushNotifications: user?.notifications?.pushNotifications,
    smsNotifications: user?.notifications?.smsNotifications,
  };

  const { handleSubmit, setValue, watch } = useForm({
    mode: "onChange",
    defaultValues,
    values: defaultValues,
  });

  const values = watch();

  const onSubmit = handleSubmit(async (values: FormValues) => {
    const { phoneNumber, ...newCustomerProfile } = values;

    const { data } = await updateCustomerProfile({
      variables: { data: newCustomerProfile },
    });

    if (data?.updateCustomerProfile) {
      appDispatch(
        editUserData({
          email: newCustomerProfile.email,
          notifications: {
            pushNotifications: newCustomerProfile.pushNotifications,
            newsletterNotifications: newCustomerProfile.newsletterNotifications,
            smsNotifications: newCustomerProfile.smsNotifications,
          },
        }),
      );
      refetch();
    }
  });

  const handleChange = useCallback(
    (fieldName: keyof FormValues) => (value: boolean) => {
      setValue(fieldName, value);
      onSubmit();
    },
    [setValue, onSubmit],
  );

  const deleteAccount = useCallback(() => {
    showActionSheetWithOptions(
      {
        title: t("screens.settings.removeAccountExplanation"),
        options: [t("screens.settings.deleteAccount"), t("common.return")],
        destructiveButtonIndex: 0,
        cancelButtonIndex: 1,
        useModal: true,
      },
      async (index) => {
        const shouldDelete = index === 0;

        if (shouldDelete) {
          const isDeleted = await deleteProfile();

          if (isDeleted) {
            appDispatch({
              type: "openModal",
              payload: {
                title: t("screens.settings.accountDeleted"),
                description: "",
                primaryAction: {
                  title: t("screens.settings.logOut"),
                  onPress: handleLogOut,
                },
              },
            });
          } else {
            alert(t("common.error"));
          }
        }
      },
    );
  }, [showActionSheetWithOptions, appDispatch, handleLogOut, deleteProfile, t]);

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Typography variant="newH2">{t("screens.settings.title")}</Typography>

      <TouchableInput
        onPress={() => setIsChangeEmailModalVisible(true)}
        placeholder={t("screens.settings.email")}
        value={values.email}
        style={styles.additionalMargin}
      />
      <Input
        editable={false}
        placeholder={t("screens.settings.phoneNumber")}
        mask="PhoneNumber"
        value={values.phoneNumber}
      />

      <Touchable onPress={() => displayIntercom()}>
        <Typography variant="lightLabel">
          {`${t("screens.settings.wantToChangePhoneNumber")} `}
          <Typography decoration="underline" type="accent" variant="lightLabel">
            {t("screens.calendar.writeToUs")}
          </Typography>
        </Typography>
      </Touchable>

      <Typography variant="newH3" style={styles.additionalMargin}>
        {t("screens.settings.notifications")}
      </Typography>

      {notificationSettings.map(({ key, labelKey, descriptionKey }, index) => (
        <Fragment key={key}>
          <Switch
            label={labelKey}
            description={descriptionKey}
            onValueChange={handleChange(key)}
            value={values[key]}
            style={index === 0 ? styles.firstSwitch : undefined}
          />
          {index < 2 && <Divider color={palette.common.disabled} />}
        </Fragment>
      ))}

      <Typography variant="newH3" style={styles.additionalMargin}>
        {t("screens.settings.others")}
      </Typography>

      {links?.map(({ href, label }) => (
        <Link key={label} href={href} style={styles.additionalMargin} target="_blank">
          <Typography>{label}</Typography>
        </Link>
      ))}

      <Touchable onPress={handleLogOut} style={styles.additionalMargin}>
        <Typography>{t("screens.settings.logOut")}</Typography>
      </Touchable>

      <Touchable onPress={deleteAccount} style={styles.additionalMargin}>
        <Typography>{t("screens.settings.deleteAccount")}</Typography>
      </Touchable>

      <ChangeEmailModal
        visible={isChangeEmailModalVisible}
        setVisible={setIsChangeEmailModalVisible}
      />
    </ScrollView>
  );
};

export default Settings;

const styles = StyleSheet.create({
  container: {
    paddingTop: spacing.unit * (isWeb ? 4 : 2),
    paddingBottom: spacing.unit * 10,
  },
  additionalMargin: {
    marginTop: spacing.unit * 4,
  },
  firstSwitch: {
    paddingTop: spacing.unit * 3,
  },
});
